import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import toast from "react-hot-toast";
import Loader from "../../../components/Loader";
import SubAdminServices from "../../../services/SubAdminServices";

const fmt = (n) => Number(n ?? 0).toLocaleString("en-IN");

/**
 * Read-only view of one sub admin with everything under them. This is
 * the whole of Super Admin's access to sub admins; the owning Admin sees
 * the same thing from the eye icon.
 */
const SubAdminDetailModal = ({ show, onClose, subAdminId }) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!show || !subAdminId) return;
    let cancelled = false;
    const api = new SubAdminServices();
    (async () => {
      setLoading(true);
      setDetail(null);
      try {
        const res = await api.detail(subAdminId);
        if (cancelled) return;
        if (res?.isSuccess) setDetail(res.data);
        else toast.error(res?.message || "Could not load sub admin");
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.message || "Could not load sub admin");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, subAdminId]);

  const UserTable = ({ title, rows, empty }) => (
    <div className="sub-admin-detail-section">
      <div className="sub-admin-detail-title">
        {title} <span className="sub-admin-detail-count">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-muted small px-1 py-2">{empty}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm sub-admin-detail-table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Status</th>
                <th className="text-end">Wallet</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td className="text-muted">{u.username}</td>
                  <td>
                    <span className={`sub-admin-pill ${u.is_active ? "on" : "off"}`}>
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="text-end">{fmt(u.wallet_balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="custom-modal">
      <div className="modal-header pb-0 pt-2">
        <h5 className="modal-title">Sub Admin Details</h5>
        <button type="button" className="btn" onClick={onClose}>
          <RiCloseLargeFill />
        </button>
      </div>
      <Modal.Body className="modal-body-scroll">
        {loading ? (
          <Loader message="Loading sub admin…" />
        ) : !detail ? (
          <div className="text-muted">No details available.</div>
        ) : (
          <>
            <div className="sub-admin-detail-grid">
              <div>
                <span>Name</span>
                <strong>{detail.name}</strong>
              </div>
              <div>
                <span>Username</span>
                <strong>{detail.username}</strong>
              </div>
              <div>
                <span>Mobile</span>
                <strong>{detail.mobile || "—"}</strong>
              </div>
              <div>
                <span>Location</span>
                <strong>{detail.location || "—"}</strong>
              </div>
              <div>
                <span>Admin</span>
                <strong>
                  {detail.admin_name || "—"}
                  {detail.admin_username ? ` (${detail.admin_username})` : ""}
                </strong>
              </div>
              <div>
                <span>Status</span>
                <strong>
                  <span className={`sub-admin-pill ${detail.is_active ? "on" : "off"}`}>
                    {detail.is_active ? "Active" : "Inactive"}
                  </span>
                </strong>
              </div>
              <div>
                <span>Wallet balance</span>
                <strong className="wallet">{fmt(detail.wallet_balance)}</strong>
              </div>
              <div>
                <span>Downline wallets</span>
                <strong>{fmt(detail.downline_balance)}</strong>
              </div>
            </div>

            <UserTable title="Outlets" rows={detail.outlets || []} empty="No outlets yet." />
            <UserTable title="Mobile users" rows={detail.mobiles || []} empty="No mobile users yet." />
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SubAdminDetailModal;
